import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { Television, Microphone, NewspaperClipping } from 'phosphor-react';
import { mediaContent } from '../data/mediaContent';
import Seo from '../components/Seo';

// Media Appearance Card Component
const MediaAppearanceCard = ({ title, date, source, description, thumbnailUrl, videoUrl, type }) => (
  <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
    <div className="aspect-w-16 aspect-h-9 bg-gray-100 relative group">
      {thumbnailUrl ? (
        <>
          <img src={thumbnailUrl} alt={title} className="object-cover w-full h-full" />
          {videoUrl && (
            <a 
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center gap-2"
            >
              <div className="bg-white rounded-lg px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors">
                Go to {source} →
              </div>
              <div className="text-white text-xs max-w-[80%] text-center truncate">
                {videoUrl}
              </div>
            </a>
          )}
        </>
      ) : (
        <div className="flex items-center justify-center bg-gray-50">
          {type === 'tv' && <Television size={48} weight="regular" className="text-terrapin-blue" />}
          {type === 'radio' && <Microphone size={48} weight="regular" className="text-terrapin-blue" />}
          {type === 'article' && <NewspaperClipping size={48} weight="regular" className="text-terrapin-blue" />}
        </div>
      )}
    </div>
    <div className="p-6">
      <div className="flex items-center gap-2 mb-2">
        {type === 'tv' && <Television size={20} weight="regular" className="text-terrapin-red" />}
        {type === 'radio' && <Microphone size={20} weight="regular" className="text-terrapin-red" />}
        {type === 'article' && <NewspaperClipping size={20} weight="regular" className="text-terrapin-red" />}
        <span className="text-sm font-medium text-gray-600">{source}</span>
      </div>
      <h3 className="text-xl font-semibold mb-2 text-terrapin-blue">{title}</h3>
      <div className="text-sm text-gray-500 mb-3">{date}</div>
      <div className="h-24 overflow-hidden mb-4">
        <p className="text-gray-600 line-clamp-4">{description}</p>
      </div>
      {videoUrl && (
        <a 
          href={videoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-terrapin-blue hover:text-terrapin-red transition-colors duration-200"
        >
          Watch on {source} →
        </a>
      )}
    </div>
  </div>
);

// Content Section Component
const ContentSection = ({ title, description, children, icon: Icon }) => (
  <section className="mb-16 last:mb-0">
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-3">
        {Icon && <Icon size={24} weight="duotone" className="text-terrapin-red" />}
        <h2 className="text-2xl font-semibold text-terrapin-blue">{title}</h2>
      </div>
      {description && <p className="text-lg text-gray-600">{description}</p>}
    </div>
    {children}
  </section>
);

const Media = () => {
  const [visibleItems, setVisibleItems] = useState(4);
  
  const handleLoadMore = () => {
    setVisibleItems(prev => Math.min(prev + 2, mediaContent.featured.length));
  };

  const hasMoreItems = visibleItems < mediaContent.featured.length;

  return (
    <>
    <Seo
      title="Tim Gillen in the Media | Terrapin Networks"
      description="See Terrapin Networks' Tim Gillen share technology insights as a trusted Northern Michigan tech expert across TV, radio, and print."
    />
    <PageLayout
      title="Media Center"
      description={
        <div className="max-w-3xl">
          <p className="text-xl mb-4">
            Tim Gillen is Northern Michigan's trusted voice on business technology
          </p>
          <p className="text-lg opacity-90">
            As a regular guest on local TV and radio, Tim shares his expertise on current technology trends, 
            cybersecurity, and practical solutions for small businesses.
          </p>
        </div>
      }
      headerStyle="default"
      contentWidth="wide"
      contentClassName="bg-white rounded-lg shadow-sm p-8"
    >
      <ContentSection 
        title="Tim's Recent TV Appearances"
        icon={Television}
        description="Catch Tim's latest segments on Good Day Northern Michigan, where he breaks down complex tech topics into practical advice for local businesses."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {mediaContent.featured.slice(0, visibleItems).map((item, index) => (
            <MediaAppearanceCard key={index} {...item} />
          ))}
        </div>
        {hasMoreItems && (
          <div className="mt-8 text-center">
            <button
              onClick={handleLoadMore}
              className="inline-flex items-center gap-2 text-terrapin-blue hover:text-terrapin-red transition-colors duration-200 text-lg font-medium"
            >
              Load More TV Appearances
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}
      </ContentSection>
        <p className="text-center italic text-gray-600 mb-8">Space below for additional media appearances or published articles</p>

      {/* <ContentSection 
        title="Radio Shows & Podcasts"
        description="Listen to Tim's insights on technology and small business success through his radio appearances and podcast episodes."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {mediaContent.radio.map((item, index) => (
            <MediaAppearanceCard key={index} {...item} />
          ))}
        </div>
      </ContentSection>

      <ContentSection 
        title="Articles & Press"
        description="Read Tim's expert commentary and advice in local publications."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {mediaContent.articles.map((item, index) => (
            <MediaAppearanceCard key={index} {...item} />
          ))}
        </div>
      </ContentSection> */}
    </PageLayout>
    </>
  );
};

export default Media; 